import { useState } from 'react';

import type { ChartCreateRequest } from '@/api/types';
import { useLang } from '@/i18n/useLang';

import { PlacePicker, type PlacePick } from './PlacePicker';

interface Props {
  defaultValues?: Partial<ChartCreateRequest>;
  onSubmit: (req: ChartCreateRequest) => void;
  isSubmitting: boolean;
  /** Optional inline error message rendered above the submit button. */
  submitError?: string | null;
}

export function ChartInputForm({ defaultValues, onSubmit, isSubmitting, submitError }: Props) {
  const { lang } = useLang();
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  const [name, setName] = useState(defaultValues?.name ?? '');
  const [dob, setDob] = useState(defaultValues?.dob ?? '');
  const [tob, setTob] = useState(defaultValues?.tob ?? '12:00:00');
  const [picked, setPicked] = useState<PlacePick | null>(null);

  const canSubmit = !!(name.trim() && dob && tob && picked && !isSubmitting);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!picked) return;
    onSubmit({
      name: name.trim(),
      dob,
      tob: tob.length === 5 ? `${tob}:00` : tob,
      place: { lat: picked.lat, lon: picked.lon },
      ayanamsa: 'lahiri',
      house_system: 'chalit',
      node: 'mean',
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid gap-3 rounded-2xl bg-stone-900/60 p-4 ring-1 ring-stone-800 sm:grid-cols-2 lg:grid-cols-12 lg:items-end"
    >
      <div className="lg:col-span-3">
        <Field
          id="name"
          label={t('नाम', 'Name')}
          value={name}
          onChange={setName}
          autoComplete="name"
          placeholder={t('जातक का नाम', "Native's name")}
          required
        />
      </div>
      <div className="lg:col-span-2">
        <Field
          id="dob"
          label={t('जन्म तिथि', 'Date of birth')}
          type="date"
          value={dob}
          onChange={setDob}
          required
        />
      </div>
      <div className="lg:col-span-2">
        <Field
          id="tob"
          label={t('जन्म समय', 'Time of birth')}
          type="time"
          step={1}
          value={tob}
          onChange={setTob}
          required
        />
      </div>
      <div className="lg:col-span-3">
        <PlacePicker value={picked} onChange={setPicked} required />
      </div>
      <div className="flex flex-col gap-1 lg:col-span-2">
        {submitError && (
          <p className="text-xs text-rose-400" role="alert">
            {submitError}
          </p>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md bg-saffron-600 px-4 py-2 font-semibold text-stone-950 transition hover:bg-saffron-500 disabled:cursor-not-allowed disabled:bg-stone-700 disabled:text-stone-500"
        >
          {isSubmitting
            ? t('गणना हो रही है…', 'Computing…')
            : t('कुंडली बनायें', 'Compute chart')}
        </button>
      </div>
    </form>
  );
}

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: number;
  autoComplete?: string;
  placeholder?: string;
  required?: boolean;
}

function Field({ id, label, value, onChange, type = 'text', step, autoComplete, placeholder, required }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[11px] font-medium uppercase tracking-wider text-saffron-400">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        required={required}
        className="rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100 outline-none transition focus:border-saffron-500 focus:ring-1 focus:ring-saffron-500 [color-scheme:dark]"
      />
    </div>
  );
}

